import type { GatewayAPI, Products, SubscriptionsTypes } from '@strim/gateway-api';

import { numericToLetters } from '@rikstv/play-common/src/utils/string-i18n/string.i18n.utils';

import { getCampaignDiscountText } from '../../../utils/campaign/campaign.utils';
import { PriceWithDiscounts, ProductDiscount } from '../../payment/forces/payment.types';
import { AddonProduct, BaseProduct, GroupedProducts } from '../hooks/useProducts';
import {
  enrichmentKeyToPackageIdMap,
  hasTV2PlaySportOrSportPremium,
  hasViaplayTotalOrSportPremium,
  isOtherVariantExclusiveAddon,
  isStrimSmartProduct,
  isTV2PlaySportPluss,
  isViaplayTotalPluss,
} from '../productEnrichments';

type MinimalProduct = Products.MinimalProduct;

type MinimalSubscription = { baseProduct: MinimalProduct; addonProducts: MinimalProduct[] };

type GetProductsNamesShorthand = {
  (baseOrSub: Products.Product[], addonProducts?: never): string;
  (baseOrSub: MinimalSubscription, addonProducts?: never): string;
  (baseOrSub: MinimalProduct, addonProducts: MinimalProduct[]): string;
};

export const getProductsNamesShorthand: GetProductsNamesShorthand = (baseOrSub, addonsOrNever) => {
  let baseProduct: MinimalProduct | undefined;
  let addonProducts: MinimalProduct[] = [];

  if (Array.isArray(baseOrSub)) {
    // Expect only one base product in array
    baseProduct = baseOrSub.find(p => p.baseProduct);
    addonProducts = baseOrSub.filter(p => !p.baseProduct);
  } else if ('addonProducts' in baseOrSub) {
    // Minimal subscription case
    baseProduct = baseOrSub.baseProduct;
    addonProducts = baseOrSub.addonProducts;
  } else {
    baseProduct = baseOrSub;
    addonProducts = addonsOrNever || [];
  }

  if (!baseProduct) {
    return '';
  }

  if (!addonProducts.length) {
    return baseProduct.name;
  }

  if (addonProducts.length === 1) {
    return `${baseProduct.name} + ${addonProducts[0].name}`;
  }

  return `${baseProduct.name} + ${numericToLetters(addonProducts.length)} tilleggspakker`;
};

export const getProductNames = ({ baseProduct, addonProducts }: MinimalSubscription) => {
  const lastProduct = addonProducts.length > 0 ? addonProducts.at(-1) : undefined;
  const middleProducts = addonProducts.length > 1 ? addonProducts.slice(0, -1) : undefined;

  const middleNames = middleProducts ? middleProducts.map(p => p.name).join(', ') : '';
  const lastName = lastProduct ? lastProduct.name : '';
  const baseProductSeparator = baseProduct && (lastProduct || middleProducts) ? ' + ' : '';
  const addonsSeparator = middleProducts ? ' og ' : '';
  const baseProductName = baseProduct?.name ?? '';

  return `${baseProductName}${baseProductSeparator}${middleNames}${addonsSeparator}${lastName}`;
};

export const getMinimalSubscriptionFromProducts = (products: Products.Product[]): MinimalSubscription => {
  const baseProduct = products.filter(p => p.baseProduct)[0];
  const addonProducts = products.filter(p => !p.baseProduct);
  return { baseProduct, addonProducts };
};

export const sumReducedAmounts = (product: Products.Product): number => {
  return sum(product.campaign?.discounts.map(d => d.reducedAmount));
};

type CampaignDiscountTier = { reducedAmount: number; numberOfBillingCycles: number };

/**
 * New customers get campaign tiers on product.campaign; existing customers get them from the
 * change summary instead, so fall back to those (campaign discounts only) keyed on productId.
 */
export const getCampaignDiscountTiers = (
  product: Products.Product,
  changeSummaryDiscounts: GatewayAPI.Models.Discount[] = []
): CampaignDiscountTier[] => {
  if (product.campaign?.discounts.length) {
    return product.campaign.discounts;
  }
  return changeSummaryDiscounts
    .filter(discount => discount.productId === product.id && !!discount.campaignId)
    .map(discount => ({
      reducedAmount: discount.amount,
      numberOfBillingCycles: discount.numberOfBillingCycles as number,
    }));
};
/**
 * Returns a sum of all campaign discounts and the lowest number from #billingCycles
 */
export const getProductDiscount = (product: Products.Product): ProductDiscount | undefined => {
  const reducedAmount = sumReducedAmounts(product);
  // select the smallest shortest number of billing cycles
  const discounts = product.campaign?.discounts || [];
  const numberOfBillingCycles = discounts.map(discount => discount.numberOfBillingCycles).sort()[0];

  if (reducedAmount === 0) {
    return undefined;
  }

  return {
    reducedAmount,
    numberOfBillingCycles,
    productId: product.id,
    isMultipleDiscounts: discounts.length > 1,
    discountDescription: getCampaignDiscountText(discounts, undefined, true),
  };
};

export const getProductsPriceAndDiscount = (products: Products.Product[]): PriceWithDiscounts => {
  const price = sum(products.map(p => p.price));
  const discounts = products.map(getProductDiscount).filter(pd => pd !== undefined);
  const totalDiscountAmount = sum(discounts.map(d => d?.reducedAmount ?? 0));

  return { price, totalDiscountAmount, discounts };
};

type SubscriptionLikeObj = {
  baseProduct?: MinimalProduct;
  addonProducts: MinimalProduct[];
};

export const getProductIds = (subscriptionLikeObj: SubscriptionLikeObj): string[] => {
  const { baseProduct, addonProducts } = subscriptionLikeObj;
  if (!baseProduct) {
    return [];
  }
  return [baseProduct.id, ...addonProducts.map(p => p.id)];
};

export const hasRelations = (
  product: Products.Product,
  otherProductIds: string | string[],
  relationType: Products.RelationType[]
) => {
  const _productIds = Array.isArray(otherProductIds) ? otherProductIds : [otherProductIds];
  if (_productIds.length === 0) return false; // If you compare with an empty array, it should return false
  if (!product.relations) return false;
  const relations = product.relations.filter(r => _productIds.includes(r.id));
  if (_productIds.length !== relations.length) {
    throw Error('Could not find a relation for each provided product id');
  }
  return relations.every(r => relationType.includes(r.relationType));
};

export const isCombinable = (currentProduct: Products.Product, otherProductIds: string | string[]) => {
  return hasRelations(currentProduct, otherProductIds, ['Combinable']);
};

export const isUncombinable = (currentProduct: Products.Product, otherProductIds: string | string[]) => {
  return hasRelations(currentProduct, otherProductIds, ['Uncombinable']);
};

export const isIncluded = (currentProduct: Products.Product, otherProductIds: string | string[]) => {
  return hasRelations(currentProduct, otherProductIds, ['Included']);
};

export const isIncludedInAny = (productId: string, otherProducts: Products.Product[]) => {
  return otherProducts.some(otherProduct =>
    otherProduct.relations.some(r => r.id === productId && r.relationType === 'Included')
  );
};

export const findRelatingProduct = (
  productId: string,
  otherProducts: Products.Product[],
  relationTypes: Products.RelationType[]
): Products.Product | undefined => {
  return otherProducts.find(otherProduct =>
    otherProduct.relations.some(relation => relation.id === productId && relationTypes.includes(relation.relationType))
  );
};

type IncludedProductInfo = {
  includedByProduct?: Products.Product;
  includedByType?: 'base' | 'addon';
};

export const getProductIncludedInfo = (
  productId: string,
  baseProduct: Products.Product | undefined,
  addonProducts: Products.Product[]
): IncludedProductInfo => {
  const allProducts = [baseProduct, ...addonProducts].filter(Boolean) as Products.Product[];

  const includedByProduct = findRelatingProduct(productId, allProducts, ['Included']);
  if (includedByProduct) {
    const isBase = baseProduct && includedByProduct.id === baseProduct.id;

    return {
      includedByProduct,
      includedByType: isBase ? 'base' : 'addon',
    };
  }

  return {};
};

export type PlussAddonDisabledReason = 'missing-tv2-play-sport' | 'missing-viaplay-total' | undefined;

export const getPlussAddonDisabledReason = (
  addonProduct: AddonProduct,
  selectedAddons: AddonProduct[]
): PlussAddonDisabledReason => {
  const selectedKeys = selectedAddons.map(a => a.enrichmentKey);
  const addonKey = addonProduct.enrichmentKey;

  if (isTV2PlaySportPluss(addonKey) && !hasTV2PlaySportOrSportPremium(selectedKeys)) {
    return 'missing-tv2-play-sport';
  }

  if (isViaplayTotalPluss(addonKey) && !hasViaplayTotalOrSportPremium(selectedKeys)) {
    return 'missing-viaplay-total';
  }
};

export type SmartVariantDisabledReason = 'other-variant-exclusive' | undefined;

export const getSmartVariantDisabledReason = (
  selectedBase: BaseProduct | undefined,
  addonProduct: AddonProduct
): SmartVariantDisabledReason => {
  if (!selectedBase || !isStrimSmartProduct(selectedBase)) return undefined;
  if (isOtherVariantExclusiveAddon(selectedBase.enrichmentKey, addonProduct.enrichmentKey)) {
    return 'other-variant-exclusive';
  }
};

export const isAddonDisabled = (
  selectedBase: BaseProduct | undefined,
  addonProduct: AddonProduct | undefined,
  selectedAddons: AddonProduct[]
) => {
  if (!selectedBase) return true;

  // !addonProduct is relevant for default choice in radio addon cards
  if (!addonProduct) return false;

  if (!isCombinable(selectedBase, addonProduct.id)) return true;

  if (isIncludedInAny(addonProduct.id, selectedAddons)) return true;

  if (getPlussAddonDisabledReason(addonProduct, selectedAddons)) return true;

  if (getSmartVariantDisabledReason(selectedBase, addonProduct)) return true;

  return false;
};

export const isBaseProductOnly = (products: Products.Product[]) => {
  // only base products are discountable
  return products.every(p => p.baseProduct);
};

export const isTrialAvailableProducts = (products: Products.Product[]) => {
  return products.every(p => p.allowInTrial);
};

export const toProductList = <T extends Required<SubscriptionLikeObj>>(enhancedProducts: T) => {
  return [enhancedProducts.baseProduct, ...enhancedProducts.addonProducts];
};

export const toMinimalProductList = (subscription: SubscriptionLikeObj) => {
  if (!subscription.baseProduct) {
    return [];
  }
  return [subscription.baseProduct, ...subscription.addonProducts];
};

// TODO: delete this can of worms
export const hackConstructOldPackageName = (products: Products.Product[]) => {
  const baseEk = products.find(p => p.baseProduct)?.enrichmentKey;
  const addonEks = products.filter(p => !p.baseProduct).map(a => a.enrichmentKey);
  if (!baseEk) {
    throw Error('Could not find base product in list of products');
  }
  const baseSubKey = enrichmentKeyToPackageIdMap.get(baseEk);
  const addonSubKeys = addonEks.map(
    ek => enrichmentKeyToPackageIdMap.get(ek) || products.find(p => p.enrichmentKey === ek)?.name
  );
  // HACK: will stop working as soon as we introduce more than one addon subscription combination
  return [baseSubKey, ...addonSubKeys].join('_') as SubscriptionsTypes.Ids;
};

export const isSellable = (subscription: SubscriptionLikeObj, products: GroupedProducts): boolean => {
  const base = subscription.baseProduct;
  const addons = subscription.addonProducts;
  if (!base) {
    return false;
  }
  const sellableBase = products.baseProducts.some(b => b.id === base.id);
  const sellableAddons = addons.every(a => products.addonProducts.some(b => b.id === a.id));
  return sellableBase && sellableAddons;
};

export interface ECommerceItem {
  id: string;
  name: string;
  price: number;
  discount: number;
  quantity: number;
}
export const toECommerceItem = (product: Products.Product): ECommerceItem => ({
  id: product.id,
  name: product.name,
  price: product.price,
  discount: sumReducedAmounts(product),
  quantity: 1,
});

const sum = (input: number[] | undefined): number =>
  input
    ? input.reduce((a, b) => {
        return a + b;
      }, 0)
    : 0;
